/* eslint-disable no-console */
import { create, StoreApi, UseBoundStore } from 'zustand';
import getErrorMessage from '../utils/error';
import { IResponse } from '../types/types';
import { getAccountsReceivable, getCurrentMonthRevenue, getMonthlyRevenue } from '../utils/api';

export interface IMonthlyRevenue {
  month: string;
  revenue: number;
}

interface IAccountsReceivable {
  totalReceivable: number;
  overdueTotal: number;
  pendingTotal: number;
  invoiceCount: number;
  overdueCount: number;
  invoices: Invoice[];
}

type Invoice = {
  invoiceId: string;
  invoiceNumber: string;
  dueDate: string;
  partnerName: string;
  amount: number;
  isOverdue: boolean;
};

interface ICurrentMonthRevenue {
  currentMonth: string;
  currentYear: number;
  revenue: number;
  previousMonthRevenue: number;
  percentageChange: number;
  trend: 'up' | 'down';
}

interface IUseAnalytics {
  loading: boolean;
  monthlyRevenue: IMonthlyRevenue[] | [];
  currentMonthRevenue: ICurrentMonthRevenue | null;
  accountsReceivable: IAccountsReceivable | null;
  fetchMonthlyRevenue: (id: string) => Promise<IResponse>;
  fetchCurrentMonthRevenue: (id: string) => Promise<IResponse>;
  fetchAccountReceivable: (id: string) => Promise<IResponse>;
}

const useAnalytics: UseBoundStore<StoreApi<IUseAnalytics>> = create((set) => ({
  loading: false,
  monthlyRevenue: [],
  currentMonthRevenue: null,
  accountsReceivable: null,
  fetchMonthlyRevenue: async (id) => {
    try {
      set(() => ({ loading: true }));
      const { success, message, data } = await getMonthlyRevenue(id);

      if (!success) {
        throw new Error(message);
      }

      set(() => ({ monthlyRevenue: data }));

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  fetchCurrentMonthRevenue: async (id) => {
    try {
      set(() => ({ loading: true }));
      const { success, message, data } = await getCurrentMonthRevenue(id);

      if (!success) {
        throw new Error(message);
      }

      set(() => ({ currentMonthRevenue: data }));

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  fetchAccountReceivable: async (id) => {
    try {
      set(() => ({ loading: true }));
      const { success, message, data } = await getAccountsReceivable(id);

      if (!success) {
        throw new Error(message);
      }

      set(() => ({ accountsReceivable: data }));

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
}));

// Subscribe untuk mengecek perubahan state
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const unsubscribe = useAnalytics.subscribe((state) => {
//   console.log('State invoice:', state);
// });

export default useAnalytics;
